import {
  ActiveOrg,
  OrganizationSelection,
  OrganizationSelectionSchema,
  User,
} from "@/schema";
import { useClerk, useAuth as useClerkAuth } from "@clerk/clerk-react";
import { useEffect, useRef, useState } from "react";
import { authenticateService } from "../services/AuthenticateService";
import { activeContextStore } from "../services/ActiveContextStore";
import { clearSessionSnapshot } from "../services/sessionSnapshot";
import fetchClient, { type FetchRequestConfig } from "../services/axiosInstance";
import { UserQuotas } from "@/schema";

// Next refresh is scheduled at (expiresAt - safeRefreshMs)
const RETRY_MS = 500;
const MAX_RETRIES = 2;
const MIN_SCHEDULED_REFRESH_MS = 30 * 1000;
const ORGANIZATION_RETRY_MS = 5 * 1000;
const ACTIVE_CONTEXT_RETRY_MS = 5 * 1000;

export function useAuth() {
  // THE APP SNAPSHOT BOOT (sweep §4): the AuthenticateService hydrates from
  // the ONE atomic session snapshot at module load — when the persisted
  // bearer is still valid AND the identity is whole, the app renders
  // INSTANTLY from it (isInit false) and the normal setupAuth flow RECONCILES
  // through the same door in the background (stale-from-snapshot is fine;
  // a real auth failure still lands loud via clearAuth/signIn). These
  // useStates initialize FROM the owner and only ever update via it — they
  // are render bindings, not a second identity store.
  const snapshotAuthed = authenticateService.isAuthenticated()
    && !!authenticateService.getCurrentUser()
    && !!authenticateService.getActiveUser()
    && !!authenticateService.getActiveOrg();
  const [isInit, setIsInit] = useState(!snapshotAuthed);
  const [user, setUser] = useState<User | null>(() => authenticateService.getCurrentUser());
  const [userQuotas, setUserQuotas] = useState<UserQuotas | null>(() => authenticateService.getUserQuotas());
  const [activeOrg, setActiveOrg] = useState<ActiveOrg | null>(() => authenticateService.getActiveOrg());
  const [activeUser, setActiveUser] = useState<User | null>(() => authenticateService.getActiveUser());
  const [tenantAttachmentError, setTenantAttachmentError] = useState<string | null>(null);
  const [isGuest, setIsGuest] = useState(false);
  const [needsOrganization, setNeedsOrganization] = useState(false);
  const [isCheckingOrganization, setIsCheckingOrganization] = useState(false);
  const [organizationSelection, setOrganizationSelection] = useState<OrganizationSelection | null>(null);
  const [organizationSelectionError, setOrganizationSelectionError] = useState<string | null>(null);
  // The current AUTH phase — the only honest status this hook can report (it does
  // auth, nothing else). Drives the page-load StatusModal title. No fabricated
  // "building model" etc. — that belongs to real world/command operations.
  const [statusText, setStatusText] = useState("Authenticating");
  const { getToken, sessionId } = useClerkAuth();
  const clerk = useClerk();
  const modalOpenRef = useRef(false);
  const refreshTimeoutRef = useRef<number | null>(null);
  const debugAuthAppliedRef = useRef(false);

  const updateUser = (user: User) => {
    authenticateService.updateCurrentUser(user);
    setUser(user);
  };
  const authenticate = async (): Promise<boolean> => {
    try {
      await authenticateService.refresh(getDebugJwt() || undefined);
      const currentUser = authenticateService.getCurrentUser();
      setUser(currentUser);
      setUserQuotas(authenticateService.getUserQuotas());
      setActiveOrg(authenticateService.getActiveOrg());
      setActiveUser(authenticateService.getActiveUser());
      setIsGuest(false);
      setNeedsOrganization(false);
      setOrganizationSelection(null);
      setOrganizationSelectionError(null);
      modalOpenRef.current = false;

      if (activeContextStore.read()?.pendingTenant) {
        setStatusText("Attaching tenant");
        setTenantAttachmentError(null);
        try {
          await activeContextStore.completePendingTenantAttachment();
        } catch (error: unknown) {
          const requestError = error && typeof error === "object"
            ? error as { message?: string; response?: { data?: { message?: string; error?: string } } }
            : undefined;
          setTenantAttachmentError(
            requestError?.response?.data?.message ||
            requestError?.response?.data?.error ||
            requestError?.message ||
            "Failed to attach the created tenant",
          );
        } finally {
          setStatusText("Authenticating");
        }
      }

      // the auth-flow marker clears on completion (analytics DIED into the
      // announcement contract — the world is the record, never a third party)
      if (currentUser) {
        localStorage.removeItem("pending_auth_mode");
      }
      return true;
    } catch (err) {
      const isNonRefreshableAuthError =
        err?.response?.status === 401 &&
        err?.response?.data?.refreshable === false;
      const isOrganizationRequired =
        err?.response?.status === 428 &&
        err?.response?.data?.code === "organization_required";
      // A 403 at the door with a STORED pair means the persisted selection is
      // no longer authorized (e.g. the membership was revoked — "You are not
      // a member of this org"). The pair travels both-or-neither, so the
      // repick path is: drop the whole stored snapshot and re-enter the SAME
      // door with no selection — a single membership resolves, multiple 428
      // into the organization picker. Never a retry loop on the dead pair
      // (after clear() there is no snapshot, so a repeat 403 falls through
      // loud instead of recursing).
      const isStaleStoredSelection =
        err?.response?.status === 403 && Boolean(activeContextStore.read());

      if (isStaleStoredSelection) {
        activeContextStore.clear();
        setActiveOrg(null);
        setActiveUser(null);
        return authenticate();
      }

      if (isOrganizationRequired) {
        const parsedSelection = OrganizationSelectionSchema.safeParse(
          err?.response?.data?.organizationSelection,
        );
        setIsGuest(false);
        setNeedsOrganization(true);
        setOrganizationSelection(parsedSelection.success ? parsedSelection.data : null);
        modalOpenRef.current = false;
        return false;
      }

      if (isNonRefreshableAuthError && !modalOpenRef.current) {
        // Reset Mixpanel to clear user identity
        activeContextStore.clear();
        setActiveOrg(null);
        setActiveUser(null);
        setIsGuest(true);
        signIn();
        return false;
      }

      throw err;
    }
  };

  const runRefreshWithRetry = async () => {
    for (let i = 0; i < MAX_RETRIES; i++) {
      try {
        const authenticated = await authenticate();
        if (!authenticated) return;
        // Successfully refreshed; schedule the next refresh by expiry-safety window
        scheduleNextRefresh();
        return;
      } catch (err) {
        if (i === MAX_RETRIES - 1) throw err; // out of retries
        await new Promise((r) => setTimeout(r, RETRY_MS)); // small back-off
      }
    }
  };

  const clearRefreshTimeout = () => {
    if (refreshTimeoutRef.current !== null) {
      window.clearTimeout(refreshTimeoutRef.current);
      refreshTimeoutRef.current = null;
    }
  };

  function getDebugJwt(): string | null {
    if (typeof window === "undefined") return null;
    const params = new URLSearchParams(window.location.search);
    const fromQuery = params.get("debug_jwt");
    if (fromQuery && fromQuery.trim()) {
      localStorage.setItem("DEBUG_FINANCIAL_JWT", fromQuery.trim());
      return fromQuery.trim();
    }
    localStorage.removeItem("DEBUG_FINANCIAL_JWT");
    return null;
  }

  const tryApplyDebugAuth = async (): Promise<boolean> => {
    // Allow Playwright / local debugging without requiring Clerk to load (which can be blocked in
    // sandboxed/headless environments).
    // Keep this local-only: never accept debug tokens outside dev/localhost.
    const isLocalHost =
      typeof window !== "undefined" &&
      ["localhost", "127.0.0.1", "0.0.0.0"].includes(window.location.hostname);
    const stageAllowsDebugAuth = import.meta.env.VITE_ENABLE_DEBUG_AUTH === "true";
    if (!stageAllowsDebugAuth && !import.meta.env.DEV && !isLocalHost) return false;
    if (debugAuthAppliedRef.current) return true;
    const debugJwt = getDebugJwt();
    if (!debugJwt) return false;
    debugAuthAppliedRef.current = true;
    try {
      // Debug tokens still cross the ONE auth door. Reuse the exact normal
      // authentication path so late-membership retries keep the same provider
      // subject instead of becoming a second local auth implementation.
      await authenticate();
      setIsInit(false);
      return true;
    } catch (error) {
      debugAuthAppliedRef.current = false;
      throw error;
    }
  };

  const scheduleNextRefresh = () => {
    // Don't schedule if we're cleaning up
    clearRefreshTimeout();
    const nextAt = authenticateService.getExpiresAtToken();
    const delay = Math.max(MIN_SCHEDULED_REFRESH_MS, nextAt - Date.now());
    refreshTimeoutRef.current = window.setTimeout(async () => {
      // Check again before executing
      await runRefreshWithRetry();
    }, delay);
  };

  const setupAuth = async () => {
    setStatusText("Authenticating");
    if (await tryApplyDebugAuth()) return;

    if (!clerk.loaded) return;
    if (!clerk.session) {
      if (!clerk.user) {
        // Acquire guest token for WS access to guest agent
        try {
          const urlParams = new URLSearchParams(window.location.search);
          await authenticateService.getGuestToken({
            company: urlParams.get('company') || undefined,
            industry: urlParams.get('industry') || undefined,
            ref: urlParams.get('ref') || undefined,
          });
        } catch (err) {
          console.warn('[guest] Failed to acquire guest token:', err);
        }
        setIsGuest(true);
        setTimeout(() => {
          setIsInit(false);
        }, 0.1);
        return;
      }
      setIsGuest(false);
      setNeedsOrganization(true);
      setIsInit(false);
      return;
    }

    // Clerk proves the actor only. The atomic local context supplies active
    // user/org; Clerk's currently selected organization never gates or changes it.
    try {
      await runRefreshWithRetry();
    } finally {
      setIsInit(false);
    }
  };

  const signUp = () => {
    modalOpenRef.current = true;
    localStorage.setItem("pending_auth_mode", "sign_up");
    clerk.openSignUp({ forceRedirectUrl: "/" });
  };

  const signIn = () => {
    modalOpenRef.current = true;
    localStorage.setItem("pending_auth_mode", "sign_in");
    clerk.openSignIn();
  };

  const signOut = async () => {
    clearRefreshTimeout();
    authenticateService.clearAuth();
    activeContextStore.clear();
    // the WHOLE session record dies with the session — auth, context, and the
    // feed cache in one atomic removal (never a stale feed for the next user).
    clearSessionSnapshot();
    localStorage.removeItem("pending_auth_mode");
    setUser(null);
    setUserQuotas(null);
    setActiveOrg(null);
    setActiveUser(null);
    setNeedsOrganization(false);
    setOrganizationSelection(null);
    setOrganizationSelectionError(null);
    setIsGuest(true);
    await clerk.signOut();
  };

  const retryOrganizationAccess = async () => {
    setIsCheckingOrganization(true);
    setOrganizationSelectionError(null);
    try {
      await runRefreshWithRetry();
    } finally {
      setIsCheckingOrganization(false);
    }
  };

  const selectOrganization = async (orgId: string) => {
    if (!organizationSelection) return;
    setIsCheckingOrganization(true);
    setOrganizationSelectionError(null);
    try {
      // The click is the only selector: persist the exact pair and both option
      // scopes first, then let the ONE auth door validate and fully hydrate it.
      activeContextStore.selectInitialOrganization(organizationSelection, orgId);
      const authenticated = await authenticate();
      if (authenticated) scheduleNextRefresh();
    } catch (error: unknown) {
      const requestError = error && typeof error === "object"
        ? error as { message?: string; response?: { data?: { message?: string } } }
        : undefined;
      setOrganizationSelectionError(
        requestError?.response?.data?.message
        || requestError?.message
        || "Failed to select organization",
      );
    } finally {
      setIsCheckingOrganization(false);
    }
  };

  useEffect(() => {
    const id = fetchClient.interceptors.request.use(async (cfg: FetchRequestConfig) => {
      const withProviderAuth =
        cfg.url?.includes("authenticate") ||
        cfg.url?.includes("admin/") === true;
      const hasExplicitProviderAuth = withProviderAuth && !!cfg.headers.Authorization;

      // For all non-provider calls, ensure our backend auth token is still valid.
      // This covers cases where the tab was suspended/backgrounded and the token expired.
      if (!withProviderAuth && !authenticateService.isAuthenticated()) {
        await runRefreshWithRetry();
      }

      const x = withProviderAuth && !hasExplicitProviderAuth
        ? await getToken()
        : undefined;
      if (withProviderAuth && !hasExplicitProviderAuth && !x) {
        throw {
          response: {
            status: 401,
            data: {
              refreshable: false,
              message: "No token found",
            },
          },
        };
      }
      if (!hasExplicitProviderAuth) {
        Object.assign(
          cfg.headers,
          authenticateService.getAuthHeaders(withProviderAuth ? x : undefined)
        );
      }
      return cfg;
    });

    return () => {
      // Clear timeout and remove listeners
      clearRefreshTimeout();
      fetchClient.interceptors.request.eject(id);
    };
  }, []);

  useEffect(() => {
    if (!clerk.loaded) return;
    setupAuth();
  }, [clerk.loaded, clerk.session?.id, clerk.user?.id, sessionId]);

  // A membership can be granted after this Clerk session was created. Retry
  // the ONE auth door with the same provider session; a successful response
  // atomically replaces the full local context and both switcher scopes.
  useEffect(() => {
    if (!needsOrganization || organizationSelection) return;
    const id = window.setInterval(() => {
      void runRefreshWithRetry().catch((error) => {
        console.warn("[auth] organization access retry failed:", error);
      });
    }, ORGANIZATION_RETRY_MS);
    return () => window.clearInterval(id);
    // The interval is intentionally keyed only by the blocked/unblocked phase;
    // AuthenticateService deduplicates refresh calls and the closure reads the
    // current page/provider token on every tick.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [needsOrganization, organizationSelection]);

  // A transient auth-door failure can settle the initial page attempt while a
  // valid cached user still has no complete active user/org pair. That is an
  // explicit hydration phase, not a completed auth state: keep re-entering the
  // ONE refresh owner until the atomic context lands (or the door moves us to
  // the organization/sign-in states). Without this, the page can wait forever
  // after a short outlet/world interruption because focus recovery sees the
  // still-valid bearer and never asks for the missing context again.
  useEffect(() => {
    if (
      isInit
      || isGuest
      || needsOrganization
      || !user
      || (activeUser && activeOrg)
    ) return;
    setStatusText("Hydrating active context");
    const retry = () => {
      void runRefreshWithRetry().catch((error) => {
        console.warn("[auth] active context hydration retry failed:", error);
      });
    };
    retry();
    const id = window.setInterval(retry, ACTIVE_CONTEXT_RETRY_MS);
    return () => window.clearInterval(id);
    // AuthenticateService deduplicates refresh calls; the dependencies name
    // only the declared phase edges that start/stop this recovery.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isInit,
    isGuest,
    needsOrganization,
    user?.id,
    activeUser?.id,
    activeOrg?.id,
  ]);

  // Apply debug auth as early as possible (before Clerk loads).
  useEffect(() => {
    void tryApplyDebugAuth();
  }, []);

  // Handle multiple recovery scenarios - refresh page if auth/connection is stale
  useEffect(() => {
    if (isGuest) return;
    const handleVisibilityChange = async () => {
      if (document.visibilityState === "visible") {
        if (needsOrganization) {
          await runRefreshWithRetry();
          return;
        }
        try {
          authenticateService.getToken();
          scheduleNextRefresh();
        } catch (error) {
          window.location.reload();
        }
      }
    };

    const handleOnline = async () => {
      if (document.visibilityState === "visible") {
        window.location.reload();
      }
    };

    // 1. Tab becomes visible (after suspension, sleep, or switch)
    document.addEventListener("visibilitychange", handleVisibilityChange);
    // 2. Network comes back online
    window.addEventListener("online", handleOnline);
    // 3. Page gains focus (additional check for some browsers)
    window.addEventListener("focus", handleVisibilityChange);
    // 4. Page resume event (fired on some mobile browsers)
    window.addEventListener("resume", handleVisibilityChange);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("focus", handleVisibilityChange);
      window.removeEventListener("resume", handleVisibilityChange);
    };
  }, [isGuest, needsOrganization]);

  const updateUserQuotas = (remainingTurns: number) => {
    setUserQuotas((prevUserQuotas) => {
      return {
        ...prevUserQuotas,
        allowTurns: remainingTurns > 0,
        remainingTurns,
      };
    });
  };

  return {
    isInitializing: isInit,
    statusText,
    user,
    isGuest,
    needsOrganization,
    isCheckingOrganization,
    organizationSelection,
    organizationSelectionError,
    selectOrganization,
    retryOrganizationAccess,
    signUp,
    signIn,
    signOut,
    activeUser,
    activeOrg,
    tenantAttachmentError,
    clearTenantAttachmentError: () => setTenantAttachmentError(null),
    updateUserQuotas,
    updateUser,
    userQuotas,
  } as const;
}
